import { cn } from '@/utils/cn'
import { focusRing, segmentedRest, segmentedSelected } from '@/components/ui/buttonStyles'

interface ThemeFilterBarProps {
  /** Themes used in this catalog. The chip row is hidden when there are none. */
  themes: string[]
  selected: string[]
  onToggle: (theme: string) => void
  query: string
  onQueryChange: (query: string) => void
}

/**
 * Theme filter and search for the reading catalog. Selecting several themes shows texts
 * with any of them; the search matches titles and theme names. Like the CEFR bar, the
 * active chip is signalled via aria-pressed and the row scrolls sideways on phones.
 */
export function ThemeFilterBar({ themes, selected, onToggle, query, onQueryChange }: ThemeFilterBarProps) {
  return (
    <div className="space-y-3 pb-3">
      <input
        type="search"
        aria-label="Search texts by title or theme"
        placeholder="Search texts or themes…"
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        className="w-full min-h-11 rounded-md border border-border-divider bg-surface-bg px-4 py-2.5 text-base sm:text-sm text-body font-light placeholder:text-label/60 focus-visible:outline-none focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent/30 transition-colors duration-200"
      />
      {themes.length > 0 && (
        <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto sm:flex-wrap [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <span className="hidden sm:inline font-mono text-[11px] uppercase tracking-[0.28em] text-muted mr-1 shrink-0">
            Theme
          </span>
          {themes.map((theme) => {
            const active = selected.includes(theme)
            return (
              <button
                key={theme}
                type="button"
                aria-pressed={active}
                onClick={() => onToggle(theme)}
                className={cn(
                  'shrink-0 min-h-11 px-3.5 inline-flex items-center font-serif text-sm rounded-full transition-colors duration-200',
                  focusRing,
                  active ? segmentedSelected : segmentedRest,
                )}
              >
                {theme}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
